import axios from 'axios';
import tokenManager from '../utils/tokenManager';
import { getApiBaseUrl } from '../config/apiBase';

// PROMPT-134: Host-Based API Calls (No localhost)
// Don't cache the base URL - get it dynamically for each request
// Version: 2.0 (2025-01-28) - Cache busted

console.log('=== API SERVICE INITIALIZATION ===');
console.log('[API Service] API will use dynamic base URL');
console.log('[API Service] Base URL determined at request time');
console.log('===================================');

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// PROMPT-134: Add request interceptor to set base URL dynamically and debug calls
api.interceptors.request.use(
  (config) => {
    // Get fresh base URL for each request
    const baseURL = getApiBaseUrl();
    config.baseURL = baseURL;
    
    console.log('=== API REQUEST DEBUG ===');
    console.log('[API Request] Base URL:', config.baseURL);
    console.log('[API Request] Endpoint:', config.url);
    console.log('[API Request] Method:', config.method);
    console.log('[API Request] Full URL:', config.baseURL + config.url);
    console.log('========================');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.request.use(
  async (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // PROMPT-113: Standardized UNAUTHORIZED error handling
    if (error.response?.status === 401) {
      // Skip 401 handling for login/OTP endpoints (prevents clearing state during OTP flow)
      const requestUrl = originalRequest?.url || '';
      if (requestUrl.includes('/login') || requestUrl.includes('/verify-otp') || requestUrl.includes('/resend-otp') || requestUrl.includes('/select-mobile')) {
        return Promise.reject(error);
      }

      // Skip 401 redirect if user is on the login page (OTP flow in progress)
      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Session expired. Please log in again.';
      
      // PROMPT-113: Handle IP whitelist enforcement
      if (errorCode === 'UNAUTHORIZED' || errorMessage.includes('IP') || errorMessage.includes('whitelist')) {
        console.error('UNAUTHORIZED: IP whitelist violation or authentication failure');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login?error=unauthorized';
        return Promise.reject(error);
      }
      
      // Clear all local storage and session storage
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expires_at');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('user');
      localStorage.removeItem('entities');
      localStorage.removeItem('selectedEntity');
      sessionStorage.clear();
      
      // Cancel any active timers
      if (window.tokenRefreshTimer) {
        clearInterval(window.tokenRefreshTimer);
        window.tokenRefreshTimer = null;
      }
      if (window.pollingTimers) {
        window.pollingTimers.forEach(timer => clearInterval(timer));
        window.pollingTimers = [];
      }
      
      // Show appropriate message and redirect
      if (errorMessage.includes('inactivity')) {
        console.log('Session expired due to inactivity');
        // Log the session expiration for debugging
        if (!window.sessionExpiredShown) {
          window.sessionExpiredShown = true;
          console.log('Your session has expired due to inactivity. Please log in again.');
        }
      } else {
        console.log('Session expired, redirecting to login');
      }
      
      window.location.href = '/login';
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password) => api.post('/login', { username, password }),
  verifyOTP: (otpToken, otp) => api.post('/login/verify-otp', { otp_token: otpToken, otp }),
  resendOTP: (otpToken) => api.post('/login/resend-otp', { otp_token: otpToken }),
  selectMobile: (otpToken, mobile) => api.post('/login/select-mobile', { otp_token: otpToken, mobile }),
  logout: (tokens) => api.post('/auth/logout', tokens), // PROMPT-047: Send both access and refresh tokens
  getProfile: () => api.get('/profile'),
  updateUIMode: (uiMode) => api.post('/update-ui-mode', { ui_mode: uiMode }), // PROMPT-114: Update UI mode preference
};

// User Management APIs
export const userAPI = {
  getAll: (page = 1, pageSize = 100) => api.get('/admin/users', { params: { page, page_size: pageSize } }),
  searchUsers: (query) => api.get('/admin/users/search', { params: { q: query } }),
  getById: (id) => api.get(`/admin/users/${id}`),
  create: (userData) => api.post('/admin/users', userData),
  update: (id, userData) => api.put(`/admin/users/${id}`, userData),
  disable: (id) => api.post(`/admin/users/${id}/disable`),
  enable: (id) => api.post(`/admin/users/${id}/enable`),
  regenerateAPIKey: (id, length = 8) => api.post(`/admin/users/${id}/regenerate-api-key`, { length }),
  getUserTemplates: (page = 1, pageSize = 20) => {
    return api.get('/user/templates', {
      params: { page, page_size: pageSize }
    });
  },
};

// Super Admin User APIs for Recharge Management
export const superUserAPI = {
  getAll: () => api.get('/super/users'),
  searchUsers: (query) => api.get('/super/users/search', { params: { q: query } }),
};

// Super Admin Recharge APIs
export const superRechargeAPI = {
  add: (rechargeData) => api.post('/super/recharge', rechargeData),
  deduct: (deductData) => api.post('/super/recharge/deduct', deductData),
  getHistory: (page, size) => api.get('/super/recharge/history', { params: { page, page_size: size } }),
  getRequests: (page, size) => api.get('/super/recharge/requests', { params: { page, page_size: size } }),
  processRequest: (id, action) => api.post(`/super/recharge/requests/${id}/process`, { action }),
};

// User Pricing / Rate Management APIs
export const pricingAPI = {
  getAll: (page, pageSize, search) => api.get('/super/pricing', { params: { page, page_size: pageSize, search } }),
  getSummary: () => api.get('/super/pricing/summary'),
  getHistory: (userId, limit) => api.get('/super/pricing/history', { params: { user_id: userId, limit } }),
  getUserPricing: (userId) => api.get(`/super/pricing/user/${userId}`),
  update: (data) => api.post('/super/pricing', data),
  bulkUpdate: (data) => api.post('/super/pricing/bulk', data),
};

// Entity APIs
export const entityAPI = {
  getAll: (page = 1, pageSize = 20) => {
    return api.get('/admin/entities', {
      params: { page, page_size: pageSize }
    });
  },
  getAllEntities: () => {
    return api.get('/admin/entities');
  },
  getById: (id) => api.get(`/admin/entities/${id}`),
  create: (entityData) => api.post('/admin/entities', entityData),
  update: (id, entityData) => api.put(`/admin/entities/${id}`, entityData),
  disable: (id) => api.post(`/admin/entities/${id}/disable`),
  enable: (id) => api.post(`/admin/entities/${id}/enable`),
  getAdminHeadersByEntity: (entityId) => {
    return api.get(`/admin/template-headers/${entityId}`);
  },
  getUserEntity: () => api.get('/user/entity'), 
  getUserEntities: () => api.get('/user/entities'),
  getAdminUserEntities: (userId) => api.get(`/admin/user-entities/${userId}`),
  getUserEntitiesForTemplate: () => {
    return api.get('/user/template-entities');
  },
  mapUserToEntity: (userId, entityId) => 
    api.post('/admin/user-entity/map', { user_id: userId, entity_id: entityId }),
  bulkMapUserToEntities: (userId, entityIds) =>
    api.post('/admin/user-entity/bulk-map', { user_id: userId, entity_ids: entityIds }),
  unmapUserFromEntity: (userId, entityId) => 
    api.post('/admin/user-entity/unmap', { user_id: userId, entity_id: entityId }),
};

// Header APIs - PROMPT-130: Enhanced with comprehensive error handling
export const headerAPI = {
  getAll: (page = 1, pageSize = 20) => {
    // PROMPT-046-FIX-02: Use role-appropriate endpoint
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? '/admin/headers' 
      : '/user/headers';
    
    console.log('[headerAPI.getAll] User role:', role);
    console.log('[headerAPI.getAll] Selected endpoint:', endpoint);
    console.log('[headerAPI.getAll] Making request to:', endpoint, 'with params:', { page, page_size: pageSize });
    
    return api.get(endpoint, {
      params: { page, page_size: pageSize }
    }).catch(error => {
      console.error('[headerAPI.getAll] API call failed:', error);
      // Enhanced error logging
      if (error.response) {
        console.error('[headerAPI.getAll] Server response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      throw error; // Re-throw to allow component-level handling
    });
  },
  getAllHeaders: (page = 1, pageSize = 20) => {
    return api.get('/admin/headers', {
      params: { page, page_size: pageSize }
    });
  },
  getHeadersByEntityForTemplate: (entityId) => {
    return api.get(`/user/entities/${entityId}/headers`);
  },
  getByEntity: (entityId) => {
    return api.get(`/admin/template-headers/${entityId}`);
  },
  getUserHeaders: () => {
    return api.get('/user/headers');
  },
  getById: (id) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? `/admin/headers/${id}` 
      : `/user/header/${id}`;
    return api.get(endpoint);
  },
  create: (headerData) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? '/admin/headers' 
      : '/user/headers';
    return api.post(endpoint, headerData);
  },
  update: (id, headerData) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? `/admin/headers/${id}` 
      : `/user/header/${id}`;
    return api.put(endpoint, headerData);
  },
  delete: (id) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? `/admin/headers/${id}` 
      : `/user/header/${id}`;
    return api.delete(endpoint);
  },
};

// Template APIs
export const templateAPI = {
  getAll: (page = 1, pageSize = 20) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? '/admin/templates' 
      : '/user/templates';
    return api.get(endpoint, {
      params: { page, page_size: pageSize }
    });
  },
  getTemplatesByHeader: (headerId) => {
    return api.get(`/user/headers/${headerId}/templates`);
  },
  getByUser: (userId) => api.get(`/admin/users/${userId}/templates`),
  getById: (id) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? `/admin/templates/${id}` 
      : `/user/templates/${id}`;
    return api.get(endpoint);
  },
  createTemplate: (data) => {
    return api.post('/admin/templates', data);
  },
  createUserTemplate: (data) => {
    return api.post('/user/templates', data);
  },
  updateTemplate: (id, data) => {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userInfo.role || '';
    const endpoint = (role === 'master_admin' || role === 'MASTER_ADMIN') 
      ? `/admin/templates/${id}` 
      : `/user/templates/${id}`;
    return api.put(endpoint, data);
  },
  deleteTemplate: (id) => {
    return api.delete(`/admin/templates/${id}`);
  },
  updateTemplateStatus: (templateId, status) => {
    return api.put(`/admin/templates/status/${templateId}`, { status });
  },
  bulkUploadTemplates: (formData) => {
    return api.post('/user/templates/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  importBulkUploadTemplates: (data) => {
    return api.post('/user/templates/bulk-upload/import', data);
  },
  cancelBulkUpload: (uploadId) => {
    return api.delete(`/user/templates/bulk-upload/${uploadId}`);
  },
  downloadTemplateSample: () => {
    return api.get('/user/templates/sample-csv', {
      responseType: 'blob'
    });
  },
  // Aliases used by BulkUploadWizard.js
  parseBulkUploadFile: (formData) => {
    return api.post('/user/templates/bulk-upload/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importUpload: (data) => {
    return api.post('/user/templates/bulk-upload/import', data);
  },
  cancelUpload: (uploadId) => {
    return api.delete(`/user/templates/bulk-upload/${uploadId}`);
  },
  downloadSampleCSV: () => {
    return api.get('/user/templates/sample-csv', {
      responseType: 'blob'
    });
  },
};

// SMS APIs
export const smsAPI = {
  send: (smsData) => api.post('/user/send-sms', smsData),
  getStatus: (messageId) => api.get(`/sms/status/${messageId}`),
  getDashboard: () => api.get('/dashboard'),
  getDynamicDashboard: () => api.get('/dashboard/dynamic'),
  
  // PROMPT-048 (FINAL): Balance with SMS Capacity
  getBalanceWithSMS: () => api.get('/user/billing/balance-with-sms'),
  refreshBalance: () => api.get('/user/billing/refresh-balance'),
  
  // PROMPT-050: SMS Traffic Chart
  getSMSTraffic: (range = 7) => api.get('/user/reports/sms/traffic', { params: { range } }),
  
  // PROMPT-051: Dashboard Metrics
  getDashboardMetrics: () => api.get('/dashboard/metrics'),
  
  // Dashboard Configuration
  getDashboardConfig: (role) => api.get(`/dashboard/config`), // Uses current user's role from token
  
  // PROMPT-125: Widget Library
  getWidgets: () => api.get('/widgets'),
  getWidgetData: (widgetCode) => api.get(`/widgets/data/${widgetCode}`),
  
  // Role Management APIs
  getRoles: () => api.get('/admin/roles'),
  getRoleById: (id) => api.get(`/admin/roles/${id}`),
  createRole: (roleData) => api.post('/admin/roles', roleData),
  updateRole: (id, roleData) => api.put(`/admin/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/admin/roles/${id}`),
  
  // Permission Management APIs
  getPermissions: () => api.get('/admin/permissions'),
  getPermissionsGrouped: () => api.get('/admin/permissions/grouped'),
  getPermissionModules: () => api.get('/admin/permissions/modules'),
  getPermissionActions: () => api.get('/admin/permissions/actions'),
  
  // User Role Assignment APIs
  getUsersWithRoles: () => api.get('/admin/users-with-roles'),
  getUserRoles: (id) => api.get(`/admin/user-roles/${id}`),
  assignRoles: (data) => api.post('/admin/user-roles/assign', data),
  removeUserRole: (userId, roleId) => api.delete(`/admin/user-roles/${userId}/remove/${roleId}`),
  
  // User Permission APIs
  getUserPermissions: (id) => api.get(`/admin/user-permissions/${id}`),
  checkUserPermission: (userId, permissionCode) => 
    api.get(`/admin/user-permissions/${userId}/check/${permissionCode}`),
  getUserPermissionOverrides: (id) => api.get(`/admin/user-permissions/${id}/overrides`),
  grantPermissionOverride: (data) => api.post('/admin/user-permissions/override', data),
  removePermissionOverride: (overrideId) => 
    api.delete(`/admin/user-permissions/overrides/${overrideId}`),
  
  // Audit Log API
  getPermissionAuditLog: (limit = 100) => 
    api.get('/admin/permissions/audit-log', { params: { limit } }),
};

// Recharge APIs
export const rechargeAPI = {
  add: (rechargeData) => api.post('/account/recharge', rechargeData),
  getHistory: (userId) => api.get('/account/recharge/history', { params: { user_id: userId } }),
  createRequest: (amount, remarks) => api.post('/recharge/request', { amount, remarks }),
  getRequests: (status) => api.get('/account/recharge/requests', { params: { status } }),
  processRequest: (id, action) => api.post(`/account/recharge/requests/${id}/process`, { action }),
};

// Support APIs
export const supportAPI = {
  createTicket: (ticketData) => api.post('/support/tickets', ticketData),
  getTickets: (status) => api.get('/support/tickets', { params: { status } }),
  getTicketById: (id) => api.get(`/support/tickets/${id}`),
  addReply: (id, message, isInternal) => 
    api.post(`/support/tickets/${id}/reply`, { message, is_internal: isInternal }),
  updateStatus: (id, status, assignedTo) => 
    api.put(`/support/tickets/${id}/status`, { status, assigned_to: assignedTo }),
};

// Plan APIs
export const planAPI = {
  getAll: () => api.get('/admin/plans'),
  getById: (id) => api.get(`/admin/plans/${id}`),
  create: (planData) => api.post('/admin/plans', planData),
  update: (id, planData) => api.put(`/admin/plans/${id}`, planData),
  delete: (id) => api.delete(`/admin/plans/${id}`),
  
  // Plan SMPP Mappings
  getSMPPMappings: (planId) => api.get(`/admin/plans/${planId}/smpp-mappings`),
  createSMPPMapping: (planId, mappingData) => api.post(`/admin/plans/${planId}/smpp-mappings`, mappingData),
  deleteSMPPMapping: (mappingId) => api.delete(`/admin/smpp-mappings/${mappingId}`),
  
  // Plan Route Mappings
  getRouteMappings: (planId) => api.get(`/admin/plans/${planId}/route-mappings`),
  createRouteMapping: (planId, mappingData) => api.post(`/admin/plans/${planId}/route-mappings`, mappingData),
  deleteRouteMapping: (mappingId) => api.delete(`/admin/route-mappings/${mappingId}`),
  
  // SMPP Connectors and Routes for dropdowns
  getSMPPConnectors: () => api.get('/admin/smpp-connectors'),
  getMessageRoutes: () => api.get('/admin/message-routes'),
  
  // User subscription management
  subscribeUser: (subscriptionData) => api.post('/admin/subscriptions/subscribe', subscriptionData),
  getUserSubscriptions: (userId) => api.get(`/admin/subscriptions/user/${userId}`),
  getUserActiveSubscription: (userId) => api.get(`/admin/subscriptions/user/${userId}/active`),
  updateSubscription: (id, subscriptionData) => api.put(`/admin/subscriptions/${id}`, subscriptionData),
};

// SMS Reports APIs
export const reportsAPI = {
  getSMSSummary: (params) => api.get('/admin/reports/sms/summary', { params }),
  getSMSDetailed: (params) => api.get('/admin/reports/sms/detailed', { params }),
  getSMSDLR: (params) => api.get('/admin/reports/sms/dlr', { params }),
  getSMSMessageDetail: (messageId) => api.get(`/admin/reports/sms/${messageId}`),
  exportSMSReport: (params) => api.get('/admin/reports/sms/export', { params }),
  getSMSTraffic: (params) => api.get('/admin/reports/sms/traffic', { params }),
};

// Login Alert Management APIs
export const loginAlertAPI = {
  getAllUsers: () => api.get('/admin/login-alerts/users'),
  getSettings: (userId) => api.get(`/admin/login-alerts/${userId}/settings`),
  updateSettings: (userId, data) => api.put(`/admin/login-alerts/${userId}/settings`, data),
  getKnownIPs: (userId) => api.get(`/admin/login-alerts/${userId}/known-ips`),
  deleteKnownIP: (id) => api.delete(`/admin/login-alerts/known-ips/${id}`),
  trustKnownIP: (id) => api.post(`/admin/login-alerts/known-ips/${id}/trust`),
  untrustKnownIP: (id) => api.post(`/admin/login-alerts/known-ips/${id}/untrust`),
  getLogs: (userId, limit) => api.get(`/admin/login-alerts/${userId}/logs`, { params: { limit } }),
  // Alert Recipient Requests — Master Admin Approval
  getRecipientRequests: (params) => api.get('/admin/alert-recipients/requests', { params }),
  approveRecipientRequest: (id) => api.post(`/admin/alert-recipients/requests/${id}/approve`),
  rejectRecipientRequest: (id, data) => api.post(`/admin/alert-recipients/requests/${id}/reject`, data),
  deleteRecipientContact: (id, data) => api.post(`/admin/alert-recipients/requests/${id}/delete`, data),
  bulkActionRecipientRequests: (data) => api.post('/admin/alert-recipients/requests/bulk-action', data),
};

// Masking Management APIs
export const maskingAPI = {
  getAllUsers: () => api.get('/admin/masking/users'),
  getSettings: (userId) => api.get(`/admin/masking/${userId}/settings`),
  updateSettings: (userId, data) => api.put(`/admin/masking/${userId}/settings`, data),
  addTemplate: (userId, data) => api.post(`/admin/masking/${userId}/templates`, data),
  deleteTemplate: (id) => api.delete(`/admin/masking/templates/${id}`),
};

// Settings & Profile APIs
export const settingsAPI = {
  getProfile: () => api.get('/user/settings/profile'),
  updateProfile: (data) => api.put('/user/settings/profile', data),
  regenerateAPIKey: (length = 16) => api.post('/user/settings/regenerate-api-key', { length }),

  // Contact Persons
  getContacts: () => api.get('/user/settings/contacts'),
  createContact: (data) => api.post('/user/settings/contacts', data),
  updateContact: (id, data) => api.put(`/user/settings/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/user/settings/contacts/${id}`),

  // Phone OTP
  sendPhoneOTP: (contactPersonId, phone) => api.post('/user/settings/phone/send-otp', { contact_person_id: contactPersonId, phone }),
  verifyPhoneOTP: (contactPersonId, phone, otp) => api.post('/user/settings/phone/verify-otp', { contact_person_id: contactPersonId, phone, otp }),

  // IP Whitelist
  getIPWhitelist: (ipType) => api.get('/user/settings/ip-whitelist', { params: { ip_type: ipType || '' } }),
  getIPRequests: () => api.get('/user/settings/ip-requests'),
  createIPRequest: (data) => api.post('/user/settings/ip-requests', data),
};

// Email Account Management APIs (Master Admin)
export const emailAccountAPI = {
  getAll: (purpose) => api.get('/admin/email-accounts', { params: { purpose: purpose || '' } }),
  getById: (id) => api.get(`/admin/email-accounts/${id}`),
  create: (data) => api.post('/admin/email-accounts', data),
  update: (id, data) => api.put(`/admin/email-accounts/${id}`, data),
  delete: (id) => api.delete(`/admin/email-accounts/${id}`),
  test: (id, toEmail) => api.post(`/admin/email-accounts/${id}/test`, { to_email: toEmail }),
};

// IP Whitelist Admin APIs
export const ipWhitelistAdminAPI = {
  getAllRequests: (status) => api.get('/admin/ip-requests', { params: { status: status || '' } }),
  approve: (id, remarks) => api.post(`/admin/ip-requests/${id}/approve`, { remarks }),
  reject: (id, remarks) => api.post(`/admin/ip-requests/${id}/reject`, { remarks }),
  updateStatus: (id, status, remarks) => api.post(`/admin/ip-requests/${id}/status`, { status, remarks }),
  getAuditLog: (userId, requestNumber) => api.get('/admin/ip-whitelist/audit-log', { params: { user_id: userId || '', request_number: requestNumber || '' } }),
};

// IP Security APIs (Global Blacklist/Whitelist, Country Rules, User Blacklist)
export const ipSecurityAPI = {
  getStats: () => api.get('/admin/ip-security/stats'),
  // Global Blacklist
  getGlobalBlacklist: (search) => api.get('/admin/ip-security/global-blacklist', { params: { search: search || '' } }),
  addGlobalBlacklist: (data) => api.post('/admin/ip-security/global-blacklist', data),
  deactivateGlobalBlacklist: (id) => api.put(`/admin/ip-security/global-blacklist/${id}/deactivate`),
  deleteGlobalBlacklist: (id) => api.delete(`/admin/ip-security/global-blacklist/${id}`),
  // Global Whitelist
  getGlobalWhitelist: (search) => api.get('/admin/ip-security/global-whitelist', { params: { search: search || '' } }),
  addGlobalWhitelist: (data) => api.post('/admin/ip-security/global-whitelist', data),
  removeGlobalWhitelist: (id) => api.delete(`/admin/ip-security/global-whitelist/${id}`),
  // Country Rules
  getCountryRules: (ruleType) => api.get('/admin/ip-security/country-rules', { params: { rule_type: ruleType || '' } }),
  addCountryRule: (data) => api.post('/admin/ip-security/country-rules', data),
  removeCountryRule: (id) => api.delete(`/admin/ip-security/country-rules/${id}`),
  // User Blacklist
  getUserBlacklist: (search, userId) => api.get('/admin/ip-security/user-blacklist', { params: { search: search || '', user_id: userId || '' } }),
  addUserBlacklist: (data) => api.post('/admin/ip-security/user-blacklist', data),
  removeUserBlacklist: (id) => api.delete(`/admin/ip-security/user-blacklist/${id}`),
  // Audit Log
  getAuditLog: (targetType, search, limit) => api.get('/admin/ip-security/audit-log', { params: { target_type: targetType || '', search: search || '', limit: limit || 200 } }),
};

// SMS Trace API
export const smsTraceAPI = {
  trace: (query) => api.get('/master-admin/sms-trace', { params: { query } }),
};

// Alert Management APIs (User side)
export const userAlertAPI = {
  getConfig: () => api.get('/user/alerts/config'),
  updateConfig: (data) => api.put('/user/alerts/config', data),
  getContacts: () => api.get('/user/alerts/contacts'),
  updateContacts: (data) => api.put('/user/alerts/contacts', data),
  sendContactOTP: (data) => api.post('/user/alerts/contacts/send-otp', data),
  verifyContactOTP: (data) => api.post('/user/alerts/contacts/verify-otp', data),
  // 2-Layer OTP Verification (DEPRECATED)
  getPrimaryContacts: () => api.get('/user/settings/primary-contacts'),
  sendPrimaryOTP: (data) => api.post('/user/alerts/contacts/send-primary-otp', data),
  verifyPrimaryOTP: (data) => api.post('/user/alerts/contacts/verify-primary-otp', data),
  sendNewContactOTP: (data) => api.post('/user/alerts/contacts/send-new-contact-otp', data),
  verifyNewContactOTP: (data) => api.post('/user/alerts/contacts/verify-new-contact-otp', data),
  // Alert Recipient Requests (Master Admin Approval Workflow)
  submitRecipientRequest: (data) => api.post('/user/alerts/recipients/request', data),
  getMyRecipientRequests: () => api.get('/user/alerts/recipients/requests'),
  deleteRecipientRequest: (id) => api.delete(`/user/alerts/recipients/requests/${id}`),
};

// Alert Management APIs (Admin side)
export const adminAlertAPI = {
  getStats: () => api.get('/admin/alerts/stats'),
  getAllConfigs: () => api.get('/admin/alerts/configs'),
  getUserConfig: (userId) => api.get(`/admin/alerts/configs/${userId}`),
  updateUserConfig: (userId, data) => api.put(`/admin/alerts/configs/${userId}`, data),
  getSchedules: () => api.get('/admin/alerts/schedules'),
  updateSchedule: (id, data) => api.put(`/admin/alerts/schedules/${id}`, data),
  getHistory: (params) => api.get('/admin/alerts/history', { params }),
  getEmailOTPLogs: (params) => api.get('/admin/alerts/email-otp-logs', { params }),
};

export default api;
